import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateBody,
  ExpandableSection,
  ExpandableSectionToggle,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { CopyIcon, DownloadIcon, SyncAltIcon } from '@patternfly/react-icons';
import type {
  CertificatePlatform,
  CertificatePlatformCaBundleChainEntry,
} from '../../../types/models/CertificatePlatform';
import { usePlatformCaBundle } from '../../../hooks/usePlatformCaBundle';
import { formatDateTime } from '../../../utils/dateFormat';
import { downloadTextFile, slugifyForFilename } from '../../../utils/fileDownload';
import { copyable } from './displayHelpers';

// Consumers gate this tab on the platform's backend-computed
// `support_ca_bundle` capability flag. A 405 from the endpoint is still
// handled below as a neutral empty state in case the tab is reached anyway.

interface PlatformCaBundleTabProps {
  platform: CertificatePlatform;
}

interface ChainEntryCardProps {
  entry: CertificatePlatformCaBundleChainEntry;
  index: number;
}

function ChainEntryCard({ entry, index }: ChainEntryCardProps) {
  const [isPemExpanded, setIsPemExpanded] = useState<boolean>(false);
  const [pemCopied, setPemCopied] = useState<boolean>(false);

  const expired = Date.parse(entry.not_after) < Date.now();
  const pemLines = entry.pem.split('\n');
  const visiblePem = pemLines[0];
  const hiddenPem = pemLines.slice(1).join('\n');
  const contentId = `ca-bundle-pem-content-${entry.platform_ref}`;
  const toggleId = `ca-bundle-pem-toggle-${entry.platform_ref}`;

  const pemActions = (
    <>
      <CodeBlockAction>
        <ClipboardCopyButton
          id={`ca-bundle-pem-copy-${entry.platform_ref}`}
          aria-label={`Copy PEM of ${entry.name}`}
          variant="plain"
          onClick={() => {
            void navigator.clipboard.writeText(`${entry.pem}\n`);
            setPemCopied(true);
          }}
          exitDelay={pemCopied ? 1500 : 600}
          onTooltipHidden={() => setPemCopied(false)}
        >
          {pemCopied ? 'Copied' : 'Copy PEM'}
        </ClipboardCopyButton>
      </CodeBlockAction>
      <CodeBlockAction>
        <Tooltip content="Download PEM">
          <Button
            variant="plain"
            aria-label={`Download PEM of ${entry.name}`}
            icon={<DownloadIcon />}
            onClick={() =>
              // The per-entry PEM arrives trimmed; written files get the
              // conventional trailing newline back.
              downloadTextFile(`${slugifyForFilename(entry.name)}.pem`, `${entry.pem}\n`)
            }
          />
        </Tooltip>
      </CodeBlockAction>
    </>
  );

  return (
    <Card>
      <CardTitle>
        {index + 1}. {entry.name}{' '}
        {index === 0 && (
          <Label color="blue" isCompact>
            Issuing CA
          </Label>
        )}{' '}
        {entry.is_self_signed && (
          <Label color="purple" isCompact>
            Root CA
          </Label>
        )}
      </CardTitle>
      <CardBody>
        <DescriptionList isHorizontal isCompact>
          <DescriptionListGroup>
            <DescriptionListTerm>Subject DN</DescriptionListTerm>
            <DescriptionListDescription>{copyable(entry.subject_dn, 'subject DN')}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Issuer DN</DescriptionListTerm>
            <DescriptionListDescription>{copyable(entry.issuer_dn, 'issuer DN')}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Key type</DescriptionListTerm>
            <DescriptionListDescription>{entry.key_type}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Valid from</DescriptionListTerm>
            <DescriptionListDescription>{formatDateTime(entry.not_before)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Valid to</DescriptionListTerm>
            <DescriptionListDescription>
              {formatDateTime(entry.not_after)}{' '}
              {expired && (
                <Label color="red" isCompact>
                  Expired
                </Label>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Serial</DescriptionListTerm>
            <DescriptionListDescription>{copyable(entry.serial, 'serial number')}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>SHA-256 fingerprint</DescriptionListTerm>
            <DescriptionListDescription>
              {copyable(entry.fingerprint_sha256, 'SHA-256 fingerprint')}
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>

        <CodeBlock actions={pemActions} className="pf-v6-u-mt-md">
          <CodeBlockCode>
            {visiblePem}
            <ExpandableSection isExpanded={isPemExpanded} isDetached contentId={contentId} toggleId={toggleId}>
              {hiddenPem}
            </ExpandableSection>
          </CodeBlockCode>
          <ExpandableSectionToggle
            isExpanded={isPemExpanded}
            onToggle={(expanded) => setIsPemExpanded(expanded)}
            contentId={contentId}
            toggleId={toggleId}
            direction="up"
          >
            {isPemExpanded ? 'Show less' : 'Show full PEM'}
          </ExpandableSectionToggle>
        </CodeBlock>
      </CardBody>
    </Card>
  );
}

/**
 * "CA Bundle" subtab of a certificate platform: the platform's live trust
 * chain (issuing CA → root) with per-certificate details and download/copy
 * actions for the concatenated bundle.
 *
 * The bundle is a live read from the upstream CA appliance, so this
 * component must only be mounted lazily (`mountOnEnter` on the parent Tabs);
 * once mounted it keeps the fetched bundle for the session and offers a
 * manual Refresh instead of refetching on tab switches.
 */
function PlatformCaBundleTab({ platform }: PlatformCaBundleTabProps) {
  const { bundle, loading, error, errorStatus, refetch } = usePlatformCaBundle(platform.id);
  const [bundleCopied, setBundleCopied] = useState<boolean>(false);
  const copiedResetHandle = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedResetHandle.current !== null) {
        window.clearTimeout(copiedResetHandle.current);
      }
    },
    [],
  );

  const copyBundle = (pem: string) => {
    void navigator.clipboard.writeText(pem);
    setBundleCopied(true);
    if (copiedResetHandle.current !== null) {
      window.clearTimeout(copiedResetHandle.current);
    }
    copiedResetHandle.current = window.setTimeout(() => setBundleCopied(false), 2000);
  };

  if (loading && bundle === null) {
    return (
      <EmptyState
        titleText="Retrieving CA bundle…"
        icon={() => <Spinner size="lg" aria-label="Retrieving CA bundle" />}
        variant="lg"
        headingLevel="h2"
      >
        <EmptyStateBody>
          Reading the trust chain live from {platform.name}. This can take a few seconds.
        </EmptyStateBody>
      </EmptyState>
    );
  }

  if (error !== null && bundle === null && errorStatus === 405) {
    // Defensive: the tab is normally hidden for platform types without bundle
    // support, but the capability is driver-side so a 405 can still arrive.
    return (
      <EmptyState titleText="Not supported by this platform" status="info" variant="lg" headingLevel="h2">
        <EmptyStateBody>This certificate platform does not expose a CA bundle.</EmptyStateBody>
      </EmptyState>
    );
  }

  const errorAlert = error !== null && (
    <Alert
      variant="danger"
      isInline
      title={
        errorStatus === 502
          ? 'The certificate platform could not provide the CA bundle'
          : 'Could not load the CA bundle'
      }
      actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
      style={{ marginBottom: '1rem' }}
    >
      {error}
    </Alert>
  );

  if (bundle === null) {
    return <div style={{ marginTop: '1rem' }}>{errorAlert}</div>;
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <Flex
        justifyContent={{ default: 'justifyContentSpaceBetween' }}
        alignItems={{ default: 'alignItemsCenter' }}
        style={{ marginBottom: '1rem' }}
      >
        <FlexItem>
          <Content component="p">
            Trust chain read live from {platform.name} — issuing CA first, root last.
          </Content>
        </FlexItem>
        <FlexItem>
          <Flex spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem>
              <Button
                variant="primary"
                icon={<DownloadIcon />}
                onClick={() =>
                  // Saved exactly as received — the bundle already carries
                  // its trailing newline.
                  downloadTextFile(`${slugifyForFilename(platform.name)}-ca-bundle.pem`, bundle.pem)
                }
              >
                Download bundle
              </Button>
            </FlexItem>
            <FlexItem>
              <Tooltip content={bundleCopied ? 'Copied' : 'Copy the concatenated PEM bundle'}>
                <Button variant="secondary" icon={<CopyIcon />} onClick={() => copyBundle(bundle.pem)}>
                  Copy bundle
                </Button>
              </Tooltip>
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                isLoading={loading}
                spinnerAriaValueText="Refreshing CA bundle"
                onClick={() => {
                  if (!loading) {
                    void refetch();
                  }
                }}
              >
                Refresh
              </Button>
            </FlexItem>
          </Flex>
        </FlexItem>
      </Flex>

      {errorAlert}

      {!bundle.complete && (
        <Alert
          variant="warning"
          isInline
          title="Certificate chain is incomplete"
          style={{ marginBottom: '1rem' }}
        >
          The platform does not hold the parent CA of the last chain entry; the bundle contains the partial chain
          only.
        </Alert>
      )}

      <Grid hasGutter>
        {bundle.chain.map((entry, index) => (
          <GridItem key={entry.platform_ref} span={12}>
            <ChainEntryCard entry={entry} index={index} />
          </GridItem>
        ))}
      </Grid>
    </div>
  );
}

export default PlatformCaBundleTab;
